import type { HelpArticle } from "../../types";
import {
  FJ1_CHECKS,
  FJ1_GOVERNING,
  FJ1_MIN_BEARING_IN,
  FJ1_REACTION_LB,
  LINK_B1_FJ1,
  MAPLE_PROJECT,
} from "../../../calculations/calcPreviewFixtures";

const pct = (value: number): string => `${Math.round(value * 100)}%`;

export const memberSheetsArticle: HelpArticle = {
  id: "member-sheets",
  title: "Wood member sheets",
  description:
    "Work through the role-aware input cards, distinguish direct and inherited loads, and read the returned design record without re-deriving it in the interface.",
  tags: ["wood", "member sheet", "inputs", "loads", "results", "reactions"],
  lastUpdated: "July 20, 2026",
  published: false,
  blocks: [
    {
      kind: "lead",
      text: "A wood member sheet is one persisted calculation record. Cards A, B, and C collect the member, its loads, and its design conditions; the right pane renders the saved engine response. The portal edits and presents that record but does not reproduce the member equations.",
    },
    { kind: "where", path: ["Calculations", MAPLE_PROJECT.name, "FJ-1"] },

    { kind: "heading", level: 2, text: "Card A describes the member", id: "inputs" },
    {
      kind: "paragraph",
      text: "The member role determines which geometry and restraint controls appear. A floor joist exposes spaced-member inputs; beams, rafters, studs, posts, and tension or beam-column roles expose the controls their adapters accept. Changing the role can therefore change both the input form and the set of checks returned by the engine.",
    },
    {
      kind: "shot",
      shotId: "member-sheet-input-cards",
      caption: "FJ-1 Card A: the active database section, the scored picker entry point, and geometry controlled by the member role.",
    },
    {
      kind: "paragraph",
      text: "A database section carries its catalog identity and reference values into the calculation. Custom section entry is a separate expert path. Typed numeric values remain local until blur or Enter; discrete selections commit when changed. A committed change recomputes the sheet and replaces the saved record only after a successful response.",
    },

    { kind: "heading", level: 2, text: "Card B keeps load source visible", id: "loads" },
    {
      kind: "paragraph",
      text: "Direct rows belong to this sheet. They retain load type, geometry, magnitude, and position. Linked rows are read-only projections of an upstream support reaction and retain their source mark, support, transfer mode, and per-load-type packet. Editing an upstream source can make the downstream sheet out of date until the graph is recomputed.",
    },
    {
      kind: "shot",
      shotId: "member-sheet-loads",
      caption: "FJ-1 Card B presents direct and inherited rows together while preserving the source of each load.",
    },
    {
      kind: "values",
      caption: "The B-1 → FJ-1 reaction packet in this worked example.",
      rows: [
        { label: "Source", value: `${LINK_B1_FJ1.sourceMark} · ${LINK_B1_FJ1.support} support` },
        { label: "Dead reaction", value: `${LINK_B1_FJ1.byType.D.toLocaleString("en-US")} lb` },
        { label: "Live reaction", value: `${LINK_B1_FJ1.byType.L.toLocaleString("en-US")} lb` },
        { label: "Transfer", value: `Line · ${LINK_B1_FJ1.spacingIn} in spacing` },
      ],
    },
    {
      kind: "callout",
      variant: "note",
      title: "Linked rows are not copied numbers",
      body: "The edge stores source and target identities plus the transfer rule. Recompute all resolves upstream sheets before their downstream consumers so the inherited packet is current.",
    },

    { kind: "heading", level: 2, text: "Card C declares calculation conditions", id: "conditions" },
    {
      kind: "paragraph",
      text: "Design method, service environment, temperature, repetitive-member selection, serviceability limits, and advanced overrides are calculation inputs. The effective method appears with the saved result. Treat the displayed criteria as the selections for this sheet, not as universal recommendations.",
    },
    {
      kind: "paragraph",
      text: "Project default leaves the sheet method inherited; ASD or LRFD creates an explicit override. When the project default changes, inherited member sheets recompute under the new method while overridden sheets retain their choice.",
    },

    { kind: "heading", level: 2, text: "The right pane is the saved record", id: "record" },
    {
      kind: "paragraph",
      text: "The result pane renders the returned verdict, checks, demands, capacities, utilization, governing combination, references, reactions, factors, assumptions, limitations, and trace when supplied. Quick and Detailed change presentation density only. Lock also changes presentation only; it does not calculate or approve the record.",
    },
    {
      kind: "shot",
      shotId: "member-sheet-record",
      caption: "FJ-1's saved check cards keep strength and serviceability demands, capacities, utilization, combinations, and references together.",
    },
    {
      kind: "values",
      caption: "FJ-1's saved check values and governing result.",
      rows: [
        ...FJ1_CHECKS.map((check) => ({
          label: check.label,
          value: `${check.demand.toLocaleString("en-US")} / ${check.capacity.toLocaleString("en-US")} ${check.units} · ${pct(check.utilization)}`,
        })),
        { label: "Governing", value: `${FJ1_GOVERNING.label} · ${pct(FJ1_GOVERNING.utilization)}` },
        { label: "Support reaction", value: `${FJ1_REACTION_LB.toLocaleString("en-US")} lb each · ${FJ1_MIN_BEARING_IN.toFixed(3)} in minimum bearing` },
      ],
    },
    {
      kind: "callout",
      variant: "warning",
      title: "Read result provenance before relying on a check",
      body: "A PASS badge is only the verdict for the returned inputs and checks. Review the method, catalog reference, warnings, assumptions, limitations, and any out-of-date or recalculation-failure banner before treating the sheet as current.",
    },

    { kind: "related", topics: ["member-picker", "design-conditions", "linking-reactions", "sheet-lifecycle"] },
  ],
};
